import { useState } from "react";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { useUpdateMatrix } from "../hooks/use-update-matrix";
import { extractErrorMessage } from "../lib/extract-error-message";

type RenameMatrixDialogProps = {
  target: { collectionId: string; name: string } | null;
  onClose: () => void;
};

// For matrices reached from a list; the open one renames in the title bar.
export function RenameMatrixDialog({
  target,
  onClose,
}: RenameMatrixDialogProps) {
  const renameMatrix = useUpdateMatrix();
  const [draft, setDraft] = useState("");
  const [lastTargetId, setLastTargetId] = useState<string | null>(null);

  const targetId = target?.collectionId ?? null;
  if (lastTargetId !== targetId) {
    setLastTargetId(targetId);
    setDraft(target?.name ?? "");
    renameMatrix.reset();
  }

  const trimmed = draft.trim();
  const canSave = trimmed !== "" && trimmed !== target?.name;

  function handleOpenChange(open: boolean) {
    if (open || renameMatrix.isPending) return;
    onClose();
  }

  function handleSave() {
    if (!target || !canSave) return;
    renameMatrix.mutate(
      { collectionId: target.collectionId, name: trimmed },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <Dialog open={target !== null} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename matrix</DialogTitle>
          <DialogDescription>
            Give "{target?.name}" a name that says what it is about.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="matrix-rename-input">Name</Label>
          <Input
            id="matrix-rename-input"
            autoFocus
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleSave();
            }}
            disabled={renameMatrix.isPending}
            spellCheck={false}
          />
        </div>
        {renameMatrix.error ? (
          <Alert variant="destructive">
            <AlertDescription>
              {extractErrorMessage(
                renameMatrix.error,
                "Failed to rename the matrix. Please try again.",
              )}
            </AlertDescription>
          </Alert>
        ) : null}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={renameMatrix.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave || renameMatrix.isPending}
          >
            {renameMatrix.isPending ? "Saving..." : "Save name"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
